import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { valuationDatesApi } from 'services/newApi/valuationDatesApi';
import { fundsApi } from 'services/newApi/fundsApi';
import { benchmarkApi } from 'services/newApi/benchmarkApi';
import { positionsApi } from 'services/newApi/positionsApi';
import { marketDataApi } from 'services/newApi/marketDataApi';
import { metricsApi } from 'services/newApi/metricsApi';
import { reportConfigsApi } from 'services/newApi/reportsConfigApi';
import { contextsApi } from 'services/newApi/contextsApi';
import { jobsApi } from 'services/newApi/jobsApi';
import { portfoliosApi } from 'services/newApi/portfoliosApi';
import { reportsDataApi } from 'services/newApi/reportsDataApi';
import { configsApi } from 'services/newApi/configsApi';
import { uiSlice, UiState } from './reducers/uiSlice';
import {
  valuationDatesSlice,
  ValuationDatesState,
} from './reducers/valuationDatesSlice';
import { fundsSlice, FundsState } from './reducers/fundsSlice';
import { benchmarkSlice, BenchmarkState } from './reducers/benchmarkSlice';
import { rtkQueryErrorLogger } from './middleware/rtkQueryErrorMiddleware';
import { marketDataSlice, MarketDataState } from './reducers/marketDataSlice';
import {
  customReportsSlice,
  CustomReportsState,
} from './reducers/customReportsSlice';
import { metricsSlice, MetricsState } from './reducers/metricsSlice';
import { contextsSlice, ContextsState } from './reducers/contextsSlice';
import { authSlice, AuthState } from './reducers/authSlice';
import { portfoliosSlice, PortfoliosState } from './reducers/portfoliosSlice';
import { configsSlice, ConfigsState } from './reducers/configsSlice';
import { jobsSlice, JobsState } from './reducers/jobsSlice';

export const store: EnhancedStore<{
  uiState: UiState;
  authState: AuthState;
  valuationDatesState: ValuationDatesState;
  fundsState: FundsState;
  benchmarkState: BenchmarkState;
  marketDataState: MarketDataState;
  customReportDataState: CustomReportsState;
  metricsState: MetricsState;
  contextsState: ContextsState;
  portfoliosSlice: PortfoliosState;
  configsState: ConfigsState;
  jobsState: JobsState;
}> = configureStore({
  reducer: {
    // Add the generated reducer as a specific top-level slice
    uiState: uiSlice.reducer,
    authState: authSlice.reducer,
    valuationDatesState: valuationDatesSlice.reducer,
    fundsState: fundsSlice.reducer,
    benchmarkState: benchmarkSlice.reducer,
    marketDataState: marketDataSlice.reducer,
    customReportDataState: customReportsSlice.reducer,
    metricsState: metricsSlice.reducer,
    contextsState: contextsSlice.reducer,
    portfoliosSlice: portfoliosSlice.reducer,
    configsState: configsSlice.reducer,
    jobsState: jobsSlice.reducer,
    [valuationDatesApi.reducerPath]: valuationDatesApi.reducer,
    [fundsApi.reducerPath]: fundsApi.reducer,
    [benchmarkApi.reducerPath]: benchmarkApi.reducer,
    [positionsApi.reducerPath]: positionsApi.reducer,
    [marketDataApi.reducerPath]: marketDataApi.reducer,
    [metricsApi.reducerPath]: metricsApi.reducer,
    [reportConfigsApi.reducerPath]: reportConfigsApi.reducer,
    [contextsApi.reducerPath]: contextsApi.reducer,
    [jobsApi.reducerPath]: jobsApi.reducer,
    [portfoliosApi.reducerPath]: portfoliosApi.reducer,
    [reportsDataApi.reducerPath]: reportsDataApi.reducer,
    [configsApi.reducerPath]: configsApi.reducer,
  },
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      rtkQueryErrorLogger,
      valuationDatesApi.middleware,
      fundsApi.middleware,
      benchmarkApi.middleware,
      positionsApi.middleware,
      marketDataApi.middleware,
      metricsApi.middleware,
      contextsApi.middleware,
      jobsApi.middleware,
      portfoliosApi.middleware,
      reportsDataApi.middleware,
      configsApi.middleware
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
